import React from "react";

import { currencySymbol, fmtDate } from "./format";
import type { SettingsPageProps } from "./OrgActions";

/** Shown when the portal will not open and the server gave no reason. */
const BILLING_PORTAL_FALLBACK = "Couldn't open the billing portal. Please try again, or contact support.";

const BillingPage: React.FC<SettingsPageProps> = ({ data, busy, run, actions }) => {
  const b = data.billing!;
  const seats = data.seats!;
  const sym = currencySymbol(b.currency);
  const interval = b.is_monthly ? "mo" : "yr";
  const total = b.price_per_user * (seats.paid || seats.contracted);

  const openPortal = () =>
    run(async () => {
      const res = await actions.billingPortal();
      if (res.portal_url) window.location.href = res.portal_url;
    }, BILLING_PORTAL_FALLBACK);

  const statusLabel = b.subscription_status
    ? b.subscription_status.replace(/_/g, " ")
    : b.trial_end_at
      ? `trial · ends ${fmtDate(b.trial_end_at)}`
      : "no subscription";

  return (
    <section className="dg-page">
      <div className="dg-eyebrow dg-ph-eye">Admin</div>
      <div className="dg-ph-title">Billing</div>
      <div className="dg-ph-sub">Your plan and usage. Manage payment and invoices in the Stripe portal.</div>

      <div className="dg-plan-hero">
        <div>
          <div className="dg-plan-name">Current plan</div>
          <div className="dg-plan-title">Dooglie</div>
          <div className="dg-plan-sub">{statusLabel}</div>
        </div>
        <div>
          <div className="dg-plan-price">
            {sym}
            {total.toFixed(0)}
            <span style={{ fontSize: 14, color: "rgba(255,255,255,.35)" }}>/{interval}</span>
          </div>
          <div className="dg-plan-sub" style={{ textAlign: "right" }}>
            {seats.paid || seats.contracted} seats × {sym}
            {b.price_per_user.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="dg-card">
        <div className="dg-card-body" style={{ padding: "14px 20px" }}>
          <div className="dg-bill-row">
            <span className="dg-bill-key">Seats given out</span>
            <span className="dg-bill-val">
              {seats.used} of {seats.paid || seats.contracted}
            </span>
          </div>
          <div className="dg-bill-row">
            <span className="dg-bill-key">Price per seat</span>
            <span className="dg-bill-val">
              {sym}
              {b.price_per_user.toFixed(2)} / {b.is_monthly ? "month" : "year"}
            </span>
          </div>
          <div className="dg-bill-row">
            <span className="dg-bill-key">Next billing date</span>
            <span className="dg-bill-val">{fmtDate(b.current_period_end)}</span>
          </div>
          <div className="dg-bill-row">
            <span className="dg-bill-key">Auto-renewal</span>
            <span className="dg-bill-val">{b.cancel_at_period_end ? "Off" : "On"}</span>
          </div>
        </div>
      </div>

      <button className="dg-btn dg-btn-primary" disabled={busy} onClick={openPortal}>
        Manage billing & invoices
      </button>
    </section>
  );
};

export default BillingPage;
